import React, { forwardRef, useMemo } from "react";
import {
  StyleProp,
  StyleSheet,
  Text,
  TextStyle,
  useWindowDimensions,
} from "react-native";
import { getConfig } from "../config/KeyManager";
import { useColors } from "../hooks";
import { ms } from "../scaling";
import { DEFAULT_FONT_SIZE_PX } from "../scaling/typographySizes";
import { TypographyProps } from "../types";
import { getFontFamily } from "../utility";

const Typography: React.FC<TypographyProps> = forwardRef(
  (
    {
      children,
      color = "dark",
      style,
      textCase,
      variant = "body1",
      align = "left",
      gutterBottom = 0,
      adjustsFontSizeToFit,
      fontWeight = 400,
      fontFamily,
      fontSize,
      lineHeight,
      ...props
    },
    ref,
  ) => {
    const colors: any = useColors();
    const { width, height } = useWindowDimensions();
    const customFontSizes = getConfig().TYPOGRAPHY?.fontSizes;

    const tabletScaleFactor = getConfig().TABLET_SCALE_FACTOR;

    const resolvedFontSize = useMemo(() => {
      if (fontSize != null) {
        return fontSize;
      }
      const designPx =
        customFontSizes?.[variant] ??
        DEFAULT_FONT_SIZE_PX[variant] ??
        DEFAULT_FONT_SIZE_PX.body1;
      return ms(designPx);
    }, [
      fontSize,
      variant,
      customFontSizes,
      width,
      height,
      tabletScaleFactor,
    ]);

    const lh = lineHeight ?? resolvedFontSize * 1.2;

    const textStyle = useMemo(
      () => ({
        lineHeight: lh,
        marginBottom: ms(gutterBottom) || 0,
        color: colors[color]?.main || color,
        textTransform: textCase,
        alignItems: "center" as const,
        textAlign: align,
        fontWeight: fontWeight,
        fontFamily: fontFamily || getFontFamily(fontWeight),
      }),
      [
        lh,
        gutterBottom,
        colors,
        color,
        textCase,
        align,
        fontWeight,
        fontFamily,
        width,
        height,
        tabletScaleFactor,
      ],
    );

    return (
      <Text
        ref={ref as any}
        allowFontScaling={false}
        adjustsFontSizeToFit={adjustsFontSizeToFit}
        style={[
          textStyle,
          style as StyleProp<TextStyle>,
          { fontSize: resolvedFontSize, lineHeight: lh },
        ]}
        {...props}
      >
        {children}
      </Text>
    );
  },
);

export default Typography;
